import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Footer from "../components/Footer";
import ContactCTA from "../components/ContactCTA";
import { Phone, Users, CheckCircle, Shield, ShieldCheck, Bug, Laptop, Bot } from "lucide-react";

// Hook for fade-in on scroll
function useScrollFadeIn() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return { ref, isVisible };
}

// AI People Counting Product Item Component
function AIPeopleCountingItem({ item, index, t }) {
  const { ref, isVisible } = useScrollFadeIn();

  return (
    <div
      ref={ref}
      className={`${index > 0 ? 'bg-gray-100' : 'bg-white'} transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 sm:px-4 py-16 sm:py-10">
        <div className="grid items-center gap-12 lg:gap-16 md:grid-cols-1 grid-cols-2">
          {/* Image - Left for first item */}
          {item.imagePosition === "left" && (
            <div className="relative overflow-hidden rounded-2xl bg-gray-50 p-8 md:p-6 sm:p-4 group">
              <img
                alt={t(`products.aiPeopleCounting.items.${item.key}.title`)}
                className="w-full rounded-lg shadow-2xl transition-transform duration-500 group-hover:scale-105"
                src={item.image}
              />
              <div className="absolute right-6 top-6 sm:right-4 sm:top-4 rounded-full bg-textBlue px-4 py-2 text-xs font-bold text-white uppercase tracking-widest">
                {item.badge}
              </div>
            </div>
          )}

          {/* Content */}
          <div>
            <h2 className="text-4xl md:text-3xl sm:text-2xl font-bold tracking-tight text-textBlue">
              {t(`products.aiPeopleCounting.items.${item.key}.title`)}
            </h2>
            <p className="mt-6 sm:mt-4 text-lg md:text-base sm:text-sm text-gray-600">
              {t(`products.aiPeopleCounting.items.${item.key}.description`)}
            </p>
            <div className="mt-8 sm:mt-6 grid gap-x-4 gap-y-3 grid-cols-2 sm:grid-cols-1">
              {t(`products.aiPeopleCounting.items.${item.key}.features`, { returnObjects: true }).map((feature, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 p-2 -m-2 rounded-lg transition-all duration-300 hover:bg-blue-50 hover:translate-x-1 cursor-default"
                >
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600 transition-colors duration-300" />
                  <span className="text-sm font-medium text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
            <a
              href={item.pdf}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-10 sm:mt-8 rounded-lg px-8 py-3 font-bold shadow-lg transition-all bg-textBlue text-white shadow-textBlue/20 hover:bg-textBlueDark"
            >
              {t("products.aiPeopleCounting.downloadDataSheet")}
            </a>
          </div>

          {/* Image - Right for second item */}
          {item.imagePosition === "right" && (
            <div className="relative overflow-hidden rounded-2xl bg-gray-50 p-8 md:p-6 sm:p-4 md:order-first group">
              <img
                alt={t(`products.aiPeopleCounting.items.${item.key}.title`)}
                className="w-full rounded-lg shadow-2xl transition-transform duration-500 group-hover:scale-105"
                src={item.image}
              />
              <div className="absolute right-6 top-6 sm:right-4 sm:top-4 rounded-full bg-textBlue px-4 py-2 text-xs font-bold text-white uppercase tracking-widest">
                {item.badge}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const products = [
  {
    icon: Phone,
    key: "communications",
    color: "text-textBlue",
    bgColor: "bg-blueLight",
  },
  {
    icon: Shield,
    key: "security",
    color: "text-red-600",
    bgColor: "bg-red-50",
  },
  {
    icon: Users,
    key: "aiPeopleCounting",
    color: "text-purple-600",
    bgColor: "bg-purple-50",
  },
];

const communicationsProducts = [
  {
    key: "skydial",
    logo: "/logo skydial.svg",
    isImage: true,
  },
  {
    key: "telephony",
    logo: "/telephony.png",
    isImage: true,
  },
  {
    key: "grandstream",
    logo: "/GRANDSTREAM.png",
    isImage: true,
    isWide: true,
  },
  {
    key: "yeastar",
    logo: "/yeastar.png",
    isImage: true,
  },
  {
    key: "voiceapi",
    logo: "/Voice API.png",
    isImage: true,
  },
  {
    key: "aichatbot",
    icon: Bot,
    isIcon: true,
  },
];

const securityProducts = [
  { key: "soc", solutionKey: "soc", icon: ShieldCheck, color: "text-red-600", bgColor: "bg-red-50" },
  { key: "vapt", solutionKey: "vapt", icon: Bug, color: "text-amber-600", bgColor: "bg-amber-50" },
  { key: "firstprotect", solutionKey: "firstprotect", icon: Laptop, color: "text-indigo-600", bgColor: "bg-indigo-50" },
];

const aiPeopleCountingProducts = [
  {
    key: "ccd21",
    image: "/CCD21.png",
    badge: "AI Vision",
    imagePosition: "left",
    pdf: "/CCD21.pdf",
  },
  {
    key: "fp221",
    image: "/FP221.png",
    badge: "TOF",
    imagePosition: "right",
    pdf: "/FP221.pdf",
  },
];

export default function ProductDetail() {
  const { t } = useTranslation();
  const { productKey } = useParams();
  const navigate = useNavigate();

  const product = products.find((p) => p.key === productKey);

  if (!product) {
    return (
      <>
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h1>
            <button
              onClick={() => navigate("/products")}
              className="text-textBlue hover:text-textBlueDark font-semibold"
            >
              Back to Products
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const Icon = product.icon;

  return (
    <>
      <div className="pt-20 bg-gradient-to-b from-white via-gray-50 to-white">
        {/* Header */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#e9f2fc] via-[#d0e7f9] to-[#c0dff7] py-16 sm:py-10 px-6 lg:px-16 md:px-8 sm:px-4">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(59,130,246,0.1),transparent_50%)]"></div>
          <div className="relative max-w-4xl mx-auto">
            <div className="flex flex-col items-start mb-6">
              <div className={`relative ${product.bgColor} w-14 h-14 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-lg -mb-5 md:-mb-2 sm:mb-1`}>
                <div className="absolute inset-0 bg-white/20 rounded-xl transform rotate-6"></div>
                <Icon className={`relative z-10 w-7 h-7 sm:w-6 sm:h-6 ${product.color}`} />
              </div>
              <h1 className="text-5xl lg:text-4xl md:text-3xl sm:text-2xl font-bold text-gray-900 leading-[1]">
                {t(`products.${product.key}.title`)}
              </h1>
              {t(`products.${product.key}.subtitle`) && (
                <p className="-mt-4 md:-mt-2 sm:mt-1 text-xl lg:text-lg md:text-base sm:text-sm text-gray-600 font-medium leading-tight">
                  {t(`products.${product.key}.subtitle`)}
                </p>
              )}
            </div>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-textBlue to-transparent rounded-full"></div>
          </div>
        </section>

        {/* Communications Products Section */}
        {product.key === "communications" && (
          <section className="max-w-7xl mx-auto px-6 py-16 md:px-12 sm:px-4">
            <div className="grid gap-6 grid-cols-3 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1">
              {communicationsProducts.map((item) => {
                const CardWrapper = item.url ? 'a' : 'div';
                const ItemIcon = item.icon;
                const cardProps = item.url ? {
                  href: item.url,
                  target: "_blank",
                  rel: "noopener noreferrer",
                } : {};

                return (
                  <CardWrapper
                    key={item.key}
                    {...cardProps}
                    className={`group flex flex-col rounded-xl border border-gray-100 bg-white p-6 transition-all hover:border-textBlue/20 hover:shadow-xl ${item.url ? 'cursor-pointer' : ''}`}
                  >
                    <div className={`mb-2 flex items-center justify-center rounded-lg bg-blueLight h-12 ${item.isWide ? 'w-32 px-2' : 'w-12'}`}>
                      {item.isIcon ? (
                        <ItemIcon className="h-6 w-6 text-textBlue" />
                      ) : (
                        <img
                          alt={t(`products.communications.items.${item.key}.title`)}
                          className={`object-contain ${item.isWide ? 'w-full h-6' : 'h-10 w-10'}`}
                          src={item.logo}
                        />
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {t(`products.communications.items.${item.key}.title`)}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {t(`products.communications.items.${item.key}.description`)}
                    </p>
                    {item.key === "skydial" && (
                      <Link
                        to="/solutions/skydial"
                        className="mt-3 inline-flex items-center gap-1 text-sm text-textBlue hover:text-textBlueDark font-semibold transition-colors"
                      >
                        {t("products.viewMore")}
                      </Link>
                    )}
                  </CardWrapper>
                );
              })}
            </div>
          </section>
        )}

        {/* Security Products Section */}
        {product.key === "security" && (
          <section className="max-w-7xl mx-auto px-6 py-16 md:px-12 sm:px-4">
            <div className="grid gap-6 grid-cols-3 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1">
              {securityProducts.map((item) => {
                const ItemIcon = item.icon;
                return (
                <div
                  key={item.key}
                  className="group flex flex-col rounded-xl border border-gray-100 bg-white p-6 transition-all hover:border-textBlue/20 hover:shadow-xl"
                >
                  <div className={`mb-2 flex items-center justify-center rounded-lg h-12 w-12 ${item.bgColor}`}>
                    <ItemIcon className={`h-6 w-6 ${item.color}`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">
                    {t(`products.security.items.${item.key}.title`)}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {t(`products.security.items.${item.key}.description`)}
                  </p>
                  <Link
                    to={`/solutions/${item.solutionKey}`}
                    className="mt-3 inline-flex items-center gap-1 text-sm text-textBlue hover:text-textBlueDark font-semibold transition-colors"
                  >
                    {t("products.viewMore")}
                  </Link>
                </div>
                );
              })}
            </div>
          </section>
        )}

        {/* AI People Counting Products Section */}
        {product.key === "aiPeopleCounting" && (
          <section>
            {aiPeopleCountingProducts.map((item, index) => (
              <AIPeopleCountingItem key={item.key} item={item} index={index} t={t} />
            ))}
          </section>
        )}

        <ContactCTA />
        <Footer />
      </div>
    </>
  );
}
